import { Monitor, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme, type Theme } from "./theme-provider";

const options: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

/** Segmented control for Light, Dark, and System. Each option has a text label for screen readers. */
export function ThemeToggle({
  collapsed = false,
  role = "radio",
}: {
  collapsed?: boolean;
  /** A menu embeds this control, and a menu's arrow keys only traverse menuitem variants. */
  role?: "radio" | "menuitemradio";
}) {
  const { theme, setTheme } = useTheme();
  return (
    <div
      role={role === "radio" ? "radiogroup" : "group"}
      aria-label="Appearance"
      className={cn("flex rounded-md bg-muted p-0.5", collapsed && "flex-col")}
    >
      {options.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role={role}
          aria-checked={theme === value}
          onClick={() => setTheme(value)}
          className={cn(
            "flex h-11 flex-1 items-center justify-center gap-1.5 rounded-sm px-2 text-xs font-medium text-muted-foreground hover:text-foreground md:h-7",
            theme === value && "bg-card text-accent-text ring-1 ring-border",
          )}
        >
          <Icon className="size-3.5" aria-hidden />
          <span className={cn(collapsed && "sr-only")}>{label}</span>
        </button>
      ))}
    </div>
  );
}
